<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

// Embute a página de autorização hospedada pela WAME, em vez de mandar o usuário
// para ela.
//
// Isto não é um truque: é o uso que eles previram. Verificado em 26/08/2026, a
// página responde `content-security-policy: frame-ancestors *` (sem
// X-Frame-Options) e avisa quem a embutiu por `postMessage`:
//
//   { type: 'WA_OFFICIAL_HEIGHT', height }  -> altura real do conteúdo
//   { type: 'WA_OFFICIAL_CONNECTED', key }  -> autorização concluída
//
// Os dois canais se comportam de forma diferente lá dentro, e a diferença é
// deles, não nossa:
//
// - **Messenger** usa `FB.login`, que abre um popup e volta pelo `postMessage`
//   acima. O ciclo inteiro termina sem sair desta tela.
// - **Instagram** faz `(window.top || window).location.href = url` — ou seja,
//   SAI do iframe por conta própria, porque o instagram.com recusa ser
//   embutido. O usuário volta pelo `return_url`, e quem lê o resultado da
//   querystring é a tela que hospeda este componente.
//
// Nos dois casos o botão passa a viver dentro do Zoraxy, que era o ponto: antes
// a autorização era uma página avulsa, com um botão solto e nada em volta.
//
// A checagem de `event.origin` é obrigatória: a página deles publica com `'*'`,
// então qualquer aba poderia forjar `WA_OFFICIAL_CONNECTED` se a gente não
// conferisse de onde veio.
const props = defineProps({
  url: {
    type: String,
    required: true,
  },
  provider: {
    type: String,
    required: true,
    validator: value => ['instagram', 'messenger'].includes(value),
  },
});

const emit = defineEmits(['connected']);

// MEDIDO EM 26/08/2026: o `WA_OFFICIAL_HEIGHT` deles é ECO, não medida. Eles
// mandam `document.body.getBoundingClientRect().height`, e o body preenche o
// iframe — então devolvem exatamente a altura que nós demos, menos a borda:
//
//   demos 150 → pediram 148 | demos 380 → pediram 378 | demos 700 → pediram 698
//
// Obedecer isso é um laço que encolhe 2px por mensagem até o piso, e foi o que
// deixou o modal com uma faixa de vazio de tamanho arbitrário.
//
// O sinal continua útil num sentido só: quando o conteúdo deles TRANSBORDA, o
// body fica maior que o iframe e o número passa a significar "preciso de mais".
// Por isso a regra é **só crescer**.
// Começa apertado de propósito: com a regra de só-crescer, um valor baixo é
// seguro — se o conteúdo deles for maior (o estado "conta conectada", com o
// cartão e o Desconectar, ou o aviso de SDK bloqueado no Messenger), o body
// passa a exceder o iframe e o próprio WA_OFFICIAL_HEIGHT vira um pedido
// legítimo de mais espaço. O caro é o contrário: sobrar moldura vazia.
const DEFAULT_HEIGHT = 120;
const GROWTH_MARGIN = 8;
const MAX_HEIGHT = 900;

const frameHeight = ref(DEFAULT_HEIGHT);

const expectedOrigin = computed(() => {
  try {
    return new URL(props.url).origin;
  } catch (error) {
    return null;
  }
});

const isMessenger = computed(() => props.provider === 'messenger');

const handleMessage = event => {
  if (!expectedOrigin.value || event.origin !== expectedOrigin.value) return;

  const data = event.data || {};

  if (data.type === 'WA_OFFICIAL_HEIGHT') {
    const height = Math.ceil(Number(data.height));
    if (!Number.isFinite(height) || height <= 0) return;
    // A margem descarta o eco (que vem 1-2px abaixo do que demos) e deixa
    // passar só o transbordo de verdade.
    if (height <= frameHeight.value + GROWTH_MARGIN) return;
    frameHeight.value = Math.min(MAX_HEIGHT, height);
    return;
  }

  if (data.type === 'WA_OFFICIAL_CONNECTED') {
    emit('connected', data.key);
  }
};

onMounted(() => window.addEventListener('message', handleMessage));
onBeforeUnmount(() => window.removeEventListener('message', handleMessage));
</script>

<template>
  <div class="w-full text-left">
    <iframe
      :src="url"
      :style="{ height: `${frameHeight}px` }"
      class="w-full border-0 rounded-xl bg-n-solid-1"
      :title="$t('INBOX_MGMT.ADD.WAME_SOCIAL.FRAME_TITLE')"
      allow="publickey-credentials-get *; clipboard-write"
      referrerpolicy="no-referrer-when-downgrade"
    />

    <p v-if="isMessenger" class="mt-2 text-xs text-n-slate-11">
      {{ $t('INBOX_MGMT.ADD.WAME_SOCIAL.FRAME_BLOCKER_HINT') }}
    </p>

    <p class="mt-2 text-xs text-n-slate-11">
      <a
        :href="url"
        target="_blank"
        rel="noopener noreferrer"
        class="underline"
      >
        {{ $t('INBOX_MGMT.ADD.WAME_SOCIAL.FRAME_FALLBACK') }}
      </a>
    </p>
  </div>
</template>
